import React, { useState, useEffect, useRef, useCallback } from 'react';
import { publicTerminalGetEmployees, publicTerminalClockIn, publicTerminalClockOut, publicTerminalGetStatus, publicTerminalGetTimeEntries, updateLocation } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Clock, Play, Square, User, CalendarDays, ChevronLeft, ChevronRight, Lock, KeyRound } from 'lucide-react';

// Terminal PIN - Ändern Sie diesen PIN nach Bedarf
const TERMINAL_PIN = "2026";

export const TerminalPage = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeStatus, setEmployeeStatus] = useState({});
    const [showHoursDialog, setShowHoursDialog] = useState(false);
    const [hoursData, setHoursData] = useState({ entries: [], weekTotal: 0, monthTotal: 0 });
    const [hoursLoading, setHoursLoading] = useState(false);
    const [selectedWeekOffset, setSelectedWeekOffset] = useState(0);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [actionLoading, setActionLoading] = useState(false);
    const [trackingActive, setTrackingActive] = useState({});
    const trackingIntervals = useRef({});
    
    // PIN protection state
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState(false);

    // Check if terminal was already unlocked in this session
    useEffect(() => {
        const unlocked = sessionStorage.getItem('terminal_unlocked');
        if (unlocked === 'true') {
            setIsUnlocked(true);
        }
    }, []);

    const handlePinSubmit = (e) => {
        e.preventDefault();
        if (pinInput === TERMINAL_PIN) {
            setIsUnlocked(true);
            sessionStorage.setItem('terminal_unlocked', 'true');
            setPinError(false);
            toast.success('Terminal entsperrt');
        } else {
            setPinError(true);
            setPinInput('');
            toast.error('Falscher PIN');
        }
    };

    // Start GPS tracking for a user
    const startTracking = (userId) => {
        if (trackingIntervals.current[userId]) return;
        
        const sendLocation = () => {
            if ('geolocation' in navigator) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        try {
                            await updateLocation({
                                user_id: userId,
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                                accuracy: position.coords.accuracy
                            });
                        } catch {
                            // Silent fail - location update optional
                        }
                    },
                    () => {
                        // Geolocation error - silent fail
                    },
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
                );
            }
        };
        
        // Send immediately
        sendLocation();
        
        // Then every 5 minutes
        trackingIntervals.current[userId] = setInterval(sendLocation, 5 * 60 * 1000);
        setTrackingActive(prev => ({ ...prev, [userId]: true }));
    };

    // Stop GPS tracking for a user
    const stopTracking = (userId) => {
        if (trackingIntervals.current[userId]) {
            clearInterval(trackingIntervals.current[userId]);
            delete trackingIntervals.current[userId];
            setTrackingActive(prev => ({ ...prev, [userId]: false }));
        }
    };

    const loadEmployees = useCallback(async () => {
        try {
            const res = await publicTerminalGetEmployees();
            // Backend already filters, but double-check
            const terminalEmployees = res.data.filter(emp => 
                emp.show_on_terminal !== false && 
                (emp.role === 'EMPLOYEE' || emp.role === 'employee')
            );
            setEmployees(terminalEmployees);
        } catch (err) {
            toast.error('Fehler beim Laden der Mitarbeiter');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAllStatuses = useCallback(async () => {
        const statuses = {};
        
        for (const emp of employees) {
            try {
                const res = await publicTerminalGetStatus(emp.id);
                statuses[emp.id] = {
                    isWorking: res.data.is_working,
                    activeEntry: res.data.active_entry,
                    todayHours: res.data.today_hours || 0
                };
            } catch (err) {
                statuses[emp.id] = { isWorking: false, activeEntry: null, todayHours: 0 };
            }
        }
        setEmployeeStatus(statuses);
    }, [employees]);

    // Cleanup on unmount
    useEffect(() => {
        const intervals = trackingIntervals.current;
        return () => {
            Object.keys(intervals).forEach(userId => {
                clearInterval(intervals[userId]);
            });
        };
    }, []);

    useEffect(() => {
        loadEmployees();
        // Update time every second
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, [loadEmployees]);

    useEffect(() => {
        if (employees.length > 0) {
            loadAllStatuses();
        }
    }, [employees, loadAllStatuses]);

    const handleClockIn = async (employee) => {
        setActionLoading(true);
        try {
            // Request location permission first
            if ('geolocation' in navigator) {
                try {
                    await new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
                    });
                } catch {
                    // Geolocation permission denied or timeout - continue without location
                }
            }
            
            // Get current date and time from the device
            const now = new Date();
            const deviceDate = now.toISOString().split('T')[0];
            const deviceTime = now.toTimeString().slice(0, 5); // HH:MM format
            
            await publicTerminalClockIn(employee.id, deviceDate, deviceTime);
            toast.success(`${employee.name} eingestempelt um ${deviceTime} Uhr!`);
            
            // Start GPS tracking
            startTracking(employee.id);
            
            await loadAllStatuses();
            setSelectedEmployee(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Einstempeln');
        } finally {
            setActionLoading(false);
        }
    };

    const handleClockOut = async (employee) => {
        setActionLoading(true);
        try {
            // Get current date and time from the device
            const now = new Date();
            const deviceDate = now.toISOString().split('T')[0];
            const deviceTime = now.toTimeString().slice(0, 5); // HH:MM format
            
            const response = await publicTerminalClockOut(employee.id, deviceDate, deviceTime);
            
            // Success - everything is automatically handled by backend
            const breakInfo = response.data.break_minutes > 0 
                ? ` (inkl. ${response.data.break_minutes} Min. Pause)`
                : '';
            toast.success(`${employee.name} ausgestempelt um ${response.data.final_end_time} Uhr!${breakInfo}`);
            
            // Stop GPS tracking
            stopTracking(employee.id);
            
            await loadAllStatuses();
            setSelectedEmployee(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Ausstempeln');
        } finally {
            setActionLoading(false);
        }
    };

    // Load hours overview for an employee
    const loadHoursOverview = async (employee, weekOffset = 0) => {
        setHoursLoading(true);
        setSelectedWeekOffset(weekOffset);
        try {
            const res = await publicTerminalGetTimeEntries(employee.id);
            const entries = res.data || [];
            
            // Calculate week boundaries with offset
            const now = new Date();
            const currentDay = now.getDay();
            const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
            
            const weekStart = new Date(now);
            weekStart.setDate(now.getDate() + mondayOffset + (weekOffset * 7));
            weekStart.setHours(0, 0, 0, 0);
            
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            weekEnd.setHours(23, 59, 59, 999);
            
            // Calculate month boundaries
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            
            // Filter entries for the selected week
            const weekEntries = entries.filter(e => {
                const entryDate = new Date(e.date);
                return entryDate >= weekStart && entryDate <= weekEnd && e.entry_type === 'WORK';
            });
            
            // Calculate week total
            const weekTotal = weekEntries.reduce((sum, e) => sum + (e.total_hours || 0), 0);
            
            // Calculate month total (always current month)
            const monthEntries = entries.filter(e => {
                const entryDate = new Date(e.date);
                return entryDate >= monthStart && entryDate <= monthEnd && e.entry_type === 'WORK';
            });
            const monthTotal = monthEntries.reduce((sum, e) => sum + (e.total_hours || 0), 0);
            
            // Build day-by-day view for the week
            const weekDays = [];
            for (let i = 0; i < 7; i++) {
                const day = new Date(weekStart);
                day.setDate(weekStart.getDate() + i);
                const dayStr = day.toISOString().split('T')[0];
                const dayEntries = weekEntries.filter(e => e.date === dayStr);
                const dayTotal = dayEntries.reduce((sum, e) => sum + (e.total_hours || 0), 0);
                
                weekDays.push({
                    date: day,
                    dateStr: dayStr,
                    dayName: day.toLocaleDateString('de-DE', { weekday: 'short' }),
                    dayNum: day.getDate(),
                    entries: dayEntries,
                    totalHours: dayTotal,
                    isToday: dayStr === new Date().toISOString().split('T')[0]
                });
            }
            
            setHoursData({
                entries: weekDays,
                weekTotal,
                monthTotal,
                weekStart,
                weekEnd
            });
        } catch (err) {
            toast.error('Fehler beim Laden der Stunden');
            setHoursData({ entries: [], weekTotal: 0, monthTotal: 0 });
        } finally {
            setHoursLoading(false);
        }
    };

    const handleShowHours = async (employee) => {
        setShowHoursDialog(true);
        await loadHoursOverview(employee, 0);
    };

    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    // PIN Entry Screen
    if (!isUnlocked) {
        return (
            <div 
                className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-6"
                data-testid="terminal-pin-screen"
            >
                <Card className="w-full max-w-md">
                    <CardContent className="p-8">
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                <Lock className="h-10 w-10 text-primary" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-800">Terminal gesperrt</h1>
                            <p className="text-muted-foreground mt-2">Bitte PIN eingeben um fortzufahren</p>
                        </div>
                        
                        <form onSubmit={handlePinSubmit} className="space-y-4">
                            <div className="relative">
                                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                <Input
                                    type="password"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="PIN eingeben"
                                    value={pinInput}
                                    onChange={(e) => {
                                        setPinInput(e.target.value);
                                        setPinError(false);
                                    }}
                                    className={`pl-10 text-center text-2xl tracking-widest h-14 ${pinError ? 'border-red-500 focus:ring-red-500' : ''}`}
                                    autoFocus
                                    data-testid="terminal-pin-input"
                                />
                            </div>
                            
                            {pinError && (
                                <p className="text-red-500 text-sm text-center">Falscher PIN. Bitte erneut versuchen.</p>
                            )}
                            
                            <Button 
                                type="submit" 
                                className="w-full h-12 text-lg"
                                data-testid="terminal-pin-submit"
                            >
                                Entsperren
                            </Button>
                        </form>
                        
                        <p className="text-xs text-muted-foreground text-center mt-6">
                            Fa. Thut Manager - Zeiterfassungs-Terminal
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div 
            className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 p-6"
            data-testid="terminal-page"
        >
            {/* Header with Time */}
            <div className="text-center mb-8">
                <div className="inline-block bg-white rounded-lg shadow-lg px-8 py-4">
                    <h1 className="text-5xl font-bold text-primary mb-1">
                        {formatTime(currentTime)}
                    </h1>
                    <p className="text-lg text-muted-foreground">
                        {formatDate(currentTime)}
                    </p>
                </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-700">
                    Zeiterfassung - Bitte Ihren Namen auswählen
                </h2>
            </div>

            {/* Employee Grid */}
            <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {employees.map((employee) => {
                    const status = employeeStatus[employee.id] || {};
                    const isWorking = status.isWorking;
                    
                    return (
                        <Card 
                            key={employee.id}
                            className={`cursor-pointer transition-all hover:scale-105 hover:shadow-xl ${
                                isWorking ? 'ring-4 ring-primary bg-primary/5' : 'bg-white'
                            }`}
                            onClick={() => setSelectedEmployee(employee)}
                            data-testid={`employee-${employee.id}`}
                        >
                            <CardContent className="p-6 text-center">
                                {/* Avatar */}
                                <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center text-2xl font-bold mb-4 ${
                                    isWorking 
                                        ? 'bg-primary text-white' 
                                        : 'bg-gray-200 text-gray-600'
                                }`}>
                                    {getInitials(employee.name)}
                                </div>
                                
                                {/* Name */}
                                <h3 className="font-semibold text-lg mb-2">{employee.name}</h3>
                                
                                {/* Status */}
                                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                    isWorking 
                                        ? 'bg-primary/20 text-primary' 
                                        : 'bg-gray-100 text-gray-500'
                                }`}>
                                    {isWorking ? (
                                        <>
                                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                                            Arbeitet
                                        </>
                                    ) : (
                                        <>
                                            <span className="w-2 h-2 bg-gray-400 rounded-full" />
                                            Nicht eingestempelt
                                        </>
                                    )}
                                </div>
                                
                                {/* Today's Hours */}
                                {status.todayHours > 0 && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Heute: {status.todayHours.toFixed(1)}h
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* No Employees */}
            {employees.length === 0 && (
                <div className="text-center py-16">
                    <User className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-xl text-muted-foreground">Keine Mitarbeiter vorhanden</p>
                </div>
            )}

            {/* Employee Action Dialog */}
            <Dialog open={!!selectedEmployee && !showHoursDialog} onOpenChange={() => setSelectedEmployee(null)}>
                <DialogContent className="max-w-md" data-testid="employee-action-dialog">
                    {selectedEmployee && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-center text-2xl">
                                    {selectedEmployee.name}
                                </DialogTitle>
                            </DialogHeader>
                            
                            <div className="space-y-4 py-4">
                                {/* Current Status */}
                                <div className="text-center p-4 rounded-lg bg-muted">
                                    {employeeStatus[selectedEmployee.id]?.isWorking ? (
                                        <div>
                                            <span className="inline-flex items-center gap-2 text-primary font-medium">
                                                <span className="w-3 h-3 bg-primary rounded-full animate-pulse" />
                                                Aktuell eingestempelt
                                            </span>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Seit {employeeStatus[selectedEmployee.id]?.activeEntry?.start_time}
                                            </p>
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground">
                                            Nicht eingestempelt
                                        </span>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="grid gap-3">
                                    {!employeeStatus[selectedEmployee.id]?.isWorking ? (
                                        <Button 
                                            size="lg" 
                                            className="h-16 text-lg"
                                            onClick={() => handleClockIn(selectedEmployee)}
                                            disabled={actionLoading}
                                            data-testid="clock-in-btn"
                                        >
                                            <Play className="h-6 w-6 mr-3" />
                                            Einstempeln
                                        </Button>
                                    ) : (
                                        <Button 
                                            size="lg" 
                                            className="h-16 text-lg bg-red-600 hover:bg-red-700"
                                            onClick={() => handleClockOut(selectedEmployee)}
                                            disabled={actionLoading}
                                            data-testid="clock-out-btn"
                                        >
                                            <Square className="h-6 w-6 mr-3" />
                                            Ausstempeln
                                        </Button>
                                    )}
                                    
                                    <Button 
                                        size="lg" 
                                        variant="outline"
                                        className="h-14"
                                        onClick={() => handleShowHours(selectedEmployee)}
                                        disabled={actionLoading}
                                        data-testid="show-hours-btn"
                                    >
                                        <CalendarDays className="h-5 w-5 mr-2" />
                                        Meine Stunden
                                    </Button>
                                </div>

                                {/* Cancel */}
                                <Button 
                                    variant="ghost" 
                                    className="w-full"
                                    onClick={() => setSelectedEmployee(null)}
                                >
                                    Abbrechen
                                </Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Hours Overview Dialog */}
            <Dialog open={showHoursDialog} onOpenChange={(open) => { if (!open) setShowHoursDialog(false); }}>
                <DialogContent className="max-w-lg" data-testid="hours-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-center text-xl flex items-center justify-center gap-2">
                            <CalendarDays className="h-6 w-6 text-primary" />
                            Stundenübersicht
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            {selectedEmployee?.name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {hoursLoading ? (
                        <div className="flex justify-center py-8">
                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <div className="space-y-4 py-2">
                            {/* Week Navigation */}
                            <div className="flex items-center justify-between bg-muted rounded-lg p-2">
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => loadHoursOverview(selectedEmployee, selectedWeekOffset - 1)}
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </Button>
                                <div className="text-center">
                                    <p className="font-medium">
                                        {hoursData.weekStart?.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} - {hoursData.weekEnd?.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {selectedWeekOffset === 0 ? 'Diese Woche' : selectedWeekOffset === -1 ? 'Letzte Woche' : `Vor ${Math.abs(selectedWeekOffset)} Wochen`}
                                    </p>
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => loadHoursOverview(selectedEmployee, selectedWeekOffset + 1)}
                                    disabled={selectedWeekOffset >= 0}
                                >
                                    <ChevronRight className="h-5 w-5" />
                                </Button>
                            </div>

                            {/* Week Days Grid */}
                            <div className="grid grid-cols-7 gap-1">
                                {hoursData.entries?.map((day) => (
                                    <div 
                                        key={day.dateStr}
                                        className={`text-center p-2 rounded-lg ${
                                            day.isToday 
                                                ? 'bg-primary text-white' 
                                                : day.totalHours > 0 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-gray-100 text-gray-500'
                                        }`}
                                    >
                                        <p className="text-xs font-medium">{day.dayName}</p>
                                        <p className="text-lg font-bold">{day.dayNum}</p>
                                        <p className={`text-xs ${day.isToday ? 'text-white/80' : ''}`}>
                                            {day.totalHours > 0 ? `${day.totalHours.toFixed(1)}h` : '-'}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            {/* Totals */}
                            <div className="grid grid-cols-2 gap-4 pt-2">
                                <div className="bg-primary/10 rounded-lg p-4 text-center">
                                    <p className="text-sm text-muted-foreground">Diese Woche</p>
                                    <p className="text-2xl font-bold text-primary">{hoursData.weekTotal?.toFixed(1)}h</p>
                                </div>
                                <div className="bg-green-100 rounded-lg p-4 text-center">
                                    <p className="text-sm text-muted-foreground">Dieser Monat</p>
                                    <p className="text-2xl font-bold text-green-700">{hoursData.monthTotal?.toFixed(1)}h</p>
                                </div>
                            </div>

                            {/* Close Button */}
                            <Button 
                                className="w-full"
                                onClick={() => setShowHoursDialog(false)}
                            >
                                Schließen
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Footer */}
            <div className="fixed bottom-4 left-0 right-0 text-center">
                <p className="text-sm text-muted-foreground">
                    Fa. Thut Manager - Zeiterfassungs-Terminal
                </p>
            </div>
        </div>
    );
};

export default TerminalPage;
